import React from 'react';
import LoadingSpinner from './ui/LoadingSpinner'; // Your LoadingSpinner
import { D3ChartConfig, TimeSeriesDataPoint } from '../types'; // Assuming types.ts is in src/
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface BettingMetricCardProps {
  title: string;
  value: string | React.ReactNode; // Value can be simple string or richer content
  isLoading?: boolean; // To show a loading state for the value itself

  // EV / Statistic related
  statistic?: string | number | null; // e.g., EV percentage, can be positive or negative
  statisticPrefix?: string; // e.g., "+", typically for positive EV
  statisticSuffix?: string; // e.g., "%"
  statisticClassName?: string; // For specific styling of the statistic (e.g., text-emerald-500, text-red-500)

  // Corner indicator (e.g., for liquidity symbol)
  cornerIndicator?: string;
  cornerIndicatorClassName?: string; // For specific styling of the corner indicator

  // Chart related props
  chartId: string; // Still needed for SessionChartView if it uses it internally for D3 selection
  chartData?: TimeSeriesDataPoint[];
  chartConfig?: D3ChartConfig;
  chartTimeScale?: string;
  isLive?: boolean; // For SessionChartView
  areChartsVisible?: boolean;
  applyBackOddsPercentage?: boolean; // For back odds charts

  // To handle the "Adjusted Back Odds" card special visibility case
  shouldHideCard?: boolean;

  className?: string; // Allow passing additional classes to the underlying Card
}

const BettingMetricCard: React.FC<BettingMetricCardProps> = ({
  title,
  value,
  isLoading = false,
  statistic,
  statisticPrefix = '',
  statisticSuffix = '',
  statisticClassName = '',
  cornerIndicator,
  cornerIndicatorClassName = '',
  chartId,
  chartData,
  chartConfig,
  chartTimeScale,
  isLive = false,
  areChartsVisible = false,
  applyBackOddsPercentage,
  shouldHideCard = false,
  className,
}) => {
  if (shouldHideCard) {
    return null;
  }

  const hasStatistic = statistic !== null && statistic !== undefined;

  // Convert your data to Chart.js format - Clean implementation based on working test
  const getChartJsData = () => {
    if (!chartData || chartData.length === 0) {
      return null;
    }

    const sampleData = chartData.slice(-7);
    const isLiquidityChart = chartConfig?.dataKey === 'total';

    if (isLiquidityChart) {
      // LIQUIDITY CHARTS: Simple case
      const liquidityValues = sampleData
        .map((point) => (point as any).total)
        .filter((val) => typeof val === 'number' && !isNaN(val));

      if (liquidityValues.length === 0) return null;

      return {
        labels: Array.from({ length: liquidityValues.length }, (_, i) =>
          (i + 1).toString()
        ),
        datasets: [
          {
            fill: 'start',
            backgroundColor: 'rgba(237, 221, 83, 0.25)',
            borderColor: '#EDDD53',
            tension: 0.3,
            borderWidth: 2,
            data: liquidityValues,
          },
        ],
      };
    } else {
      // EV CHARTS: Extract EV percentage values
      const extractedValues = sampleData.map((point) => {
        const pointAny = point as any;
        switch (chartConfig?.dataKey) {
          case 'layEV':
            return pointAny.layEV;
          case 'midpointEV':
            return pointAny.midpointEV;
          case 'adjustedEV':
            return pointAny.adjustedEV;
          default:
            return null;
        }
      });

      const validEVValues = extractedValues.filter(
        (val) => typeof val === 'number' && !isNaN(val)
      );

      // Use historical data if available, otherwise create simple trend around current EV
      let finalValues: number[];
      if (validEVValues.length === 0) {
        const currentEV =
          typeof statistic === 'number'
            ? statistic
            : parseFloat(String(statistic || 0));

        if (isNaN(currentEV)) return null;

        // Create simple trend around current EV
        finalValues = Array.from({ length: 7 }, () => {
          const variation = (Math.random() - 0.5) * 2; // ±1% variation
          return Math.round((currentEV + variation) * 100) / 100;
        });
      } else {
        finalValues = validEVValues;
      }

      // Build chart with segment-based coloring like our working test
      return {
        labels: Array.from({ length: finalValues.length }, (_, i) =>
          (i + 1).toString()
        ),
        datasets: [
          {
            fill: 'origin',
            backgroundColor: function (context: any) {
              const value = finalValues[context.dataIndex];
              return value >= 0
                ? 'rgba(87, 199, 133, 0.25)'
                : 'rgba(239, 68, 68, 0.25)';
            },
            borderColor: function (context: any) {
              const value = finalValues[context.dataIndex];
              return value >= 0 ? '#57C785' : '#EF4444';
            },
            segment: {
              borderColor: function (ctx: any) {
                const endValue = finalValues[ctx.p1DataIndex];
                return endValue >= 0 ? '#57C785' : '#EF4444';
              },
              backgroundColor: function (ctx: any) {
                const endValue = finalValues[ctx.p1DataIndex];
                return endValue >= 0
                  ? 'rgba(87, 199, 133, 0.25)'
                  : 'rgba(239, 68, 68, 0.25)';
              },
            },
            tension: 0.3,
            borderWidth: 2,
            data: finalValues,
          },
        ],
      };
    }
  };

  const chartJsData = getChartJsData();

  // Check if the main value is N/A
  const valueString = typeof value === 'string' ? value : String(value);
  const isValueNA =
    valueString === 'N/A' || valueString === '' || valueString === '-';
  const shouldShowChart = areChartsVisible && chartJsData && !isValueNA;

  // Calculate dynamic Y-axis range based on actual data
  const getYAxisRange = () => {
    if (!chartJsData || !chartJsData.datasets[0]?.data) {
      return { min: -5, max: 5 };
    }

    const values = chartJsData.datasets[0].data as number[];
    if (values.length === 0) {
      return { min: -5, max: 5 };
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    const isLiquidityChart = chartConfig?.dataKey === 'total';

    if (isLiquidityChart) {
      const padding = Math.max(max * 0.2, 10);
      return { min: 0, max: max + padding };
    } else {
      // EV charts: Always center zero properly, especially after resets
      const absMax = Math.max(Math.abs(min), Math.abs(max));

      // Ensure minimum range when data is sparse (like after reset)
      const minRange = Math.max(absMax * 0.3, 3); // At least ±3% range

      // Create balanced range around zero
      const range = Math.max(minRange, absMax + 1); // Add 1% buffer minimum

      return {
        min: -range,
        max: range,
      };
    }
  };

  const yAxisRange = getYAxisRange();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    elements: {
      point: {
        radius: 0,
      },
    },
    scales: {
      x: {
        display: false,
      },
      y: {
        display: false,
        min: yAxisRange.min,
        max: yAxisRange.max,
        grid: {
          display: chartConfig?.dataKey !== 'total', // Show grid for EV charts only
          drawOnChartArea: true,
          color: function (context: any) {
            // Highlight the zero line for EV charts
            if (context.tick.value === 0) {
              return 'rgba(0, 0, 0, 0.5)'; // More visible zero line
            }
            return 'rgba(0, 0, 0, 0)'; // Hide other grid lines
          },
          lineWidth: function (context: any) {
            return context.tick.value === 0 ? 2 : 0; // Thicker zero line
          },
        },
      },
    },
  };

  // Determine if statistic is positive or negative for styling
  const getStatisticStyle = () => {
    if (!hasStatistic) return 'text-gray-500';

    const numericStatistic =
      typeof statistic === 'number'
        ? statistic
        : parseFloat(statistic as string);
    if (isNaN(numericStatistic)) return 'text-gray-500';

    if (numericStatistic === 0) return 'text-gray-500';
    return numericStatistic > 0 ? 'text-green-500' : 'text-red-500';
  };

  return (
    <div className='relative p-5 pb-16 overflow-hidden bg-surface-light dark:bg-surface-dark rounded-md shadow-lg'>
      {cornerIndicator && (
        <div
          className={`absolute top-0 right-0 px-3 py-1.5 rounded-bl-lg rounded-tr-lg text-xs font-semibold z-10 ${cornerIndicatorClassName}`}
        >
          {cornerIndicator}
        </div>
      )}

      <div className='text-base text-text-secondary-light dark:text-text-secondary-dark'>
        {title}
      </div>
      <div className='relative z-10 flex items-center pt-1'>
        <div className='text-2xl font-bold text-text-primary-light dark:text-text-primary-dark'>
          {isLoading ? (
            <div className='flex justify-center items-center h-8'>
              <LoadingSpinner size='sm' />
            </div>
          ) : (
            value
          )}
        </div>
        {hasStatistic && (
          <span
            className={`px-2 py-0.5 mx-2 text-sm font-medium ${getStatisticStyle()}`}
          >
            {statisticPrefix}
            {statistic}
            {statisticSuffix}
          </span>
        )}
      </div>

      {shouldShowChart && (
        <div className='absolute bottom-0 inset-x-0 z-0'>
          <Line height={80} data={chartJsData} options={chartOptions} />
        </div>
      )}
    </div>
  );
};

export default BettingMetricCard;
